import React, { useState } from 'react';
import { KeyRound, AlertTriangle } from 'lucide-react';
import { SUPABASE_AUTH_ENABLED } from '../../../../data/supabase/session';
import { Button } from '../../../../components/ui/Button';
import { Input } from '../../../../components/ui/Input';
import { Select } from '../../../../components/ui/Select';
import { generateUniqueLinkCode, suggestUsername, hashPassword, sanitizeDisplayName } from '../../../../accounts';
import type { AccountsState, AccountRole } from '../../../../accounts';
import type { CreateAccountDetails, CreateAccountOptions } from '../../../../app/store/types';
interface AddUserFormProps {
  accounts: AccountsState | undefined;
  store: {
    createAccount: (role: AccountRole, details: CreateAccountDetails, options?: CreateAccountOptions) => string;
  };
  onClose: () => void;
}
const MAX_NAME_LENGTH = 100;
const MIN_NAME_LENGTH = 2;
const MIN_PASSWORD_LENGTH = 4;
export function AddUserForm({
  accounts,
  store,
  onClose
}: AddUserFormProps) {
  // Dit formulier maakt een account uitsluitend in localStorage, met de oude
  // ongesalte SHA-256-hash. Sinds de inlog via Supabase loopt, levert dat een
  // spookaccount op: het verschijnt in het overzicht, maar er is geen rij in
  // `auth.users` en geen profielrij — dus niemand kan ermee inloggen, en het
  // valt buiten `data_controller`, `consent_status` en elke bewaartermijn.
  //
  // Een leerlingaccount dat het hele governancemodel omzeilt is precies wat we
  // hebben proberen uit te sluiten. Aanmaken hoort serverside te gebeuren via
  // de Edge Function `create-learner` (service-role, `email_confirm: true`);
  // tot die er is, is stilzwijgend doorgaan slechter dan weigeren.
  const [newUserRole, setNewUserRole] = useState<'learner' | 'teacher' | 'parent' | 'admin'>('learner');
  const [newUserName, setNewUserName] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserSchool, setNewUserSchool] = useState('');
  const [newUserGrade, setNewUserGrade] = useState('6');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [validationError, setValidationError] = useState('');

  // Na de hooks, niet ervoor: de hook-volgorde moet gelijk blijven.
  if (SUPABASE_AUTH_ENABLED) {
    return <div className="space-y-4" data-id="element-7457" data-mp="13hx2so69bof6">
        <div className="flex gap-3 p-4 rounded-xl bg-amber-50 border border-amber-200" data-id="element-7458" data-mp="13gm2iyc0syst">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" aria-hidden="true" data-id="element-7459" data-mp="1ie4oxu186zx3" />
          <div className="text-sm text-amber-900" data-id="element-7460" data-mp="1dfkxje3v4vxc">
            <p className="font-bold mb-1" data-id="element-7461" data-mp="1r7rciwva4wv7">
              Handmatig aanmaken is tijdelijk uitgeschakeld
            </p>
            <p data-id="element-7462" data-mp="1n6pe2i96soua">
              Accounts worden nu op de server aangemaakt. Een account dat hier
              wordt gemaakt, zou alleen op dit apparaat bestaan — er kan dan
              niemand mee inloggen. Laat gebruikers zich registreren, of koppel
              een leerling met een klas- of koppelcode.
            </p>
          </div>
        </div>
        <div className="flex justify-end" data-id="element-7463" data-mp="2fq9z3c0vmlxm">
          <Button type="button" variant="secondary" onClick={onClose} data-id="element-7464" data-mp="2kq6155q0k3aj">
            Sluiten
          </Button>
        </div>
      </div>;
  }
  const handleAddUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError('');
    const trimmedName = sanitizeDisplayName(newUserName.trim());
    if (trimmedName.length < MIN_NAME_LENGTH) {
      setValidationError(`Naam moet minimaal ${MIN_NAME_LENGTH} tekens zijn.`);
      return;
    }
    if (trimmedName.length > MAX_NAME_LENGTH) {
      setValidationError(`Naam mag maximaal ${MAX_NAME_LENGTH} tekens zijn.`);
      return;
    }
    if (newUserRole === 'learner') {
      const pw = newUserPassword.trim();
      if (pw && pw.length < MIN_PASSWORD_LENGTH) {
        setValidationError(`Wachtwoord moet minimaal ${MIN_PASSWORD_LENGTH} tekens zijn.`);
        return;
      }
      const linkCode = generateUniqueLinkCode(accounts);
      const username = suggestUsername(trimmedName);
      const passwordHash = pw ? await hashPassword(pw) : undefined;
      store.createAccount(newUserRole, {
        displayName: trimmedName,
        email: newUserEmail || undefined,
        grade: parseInt(newUserGrade, 10),
        linkCode,
        username,
        passwordHash
      });
    } else {
      store.createAccount(newUserRole, {
        displayName: trimmedName,
        email: newUserEmail || undefined,
        schoolName: newUserSchool || undefined
      });
    }
    onClose();
  };
  return <form onSubmit={handleAddUser} className="space-y-4" data-id="element-7465" data-mp="ibp29i8ie1cc">
      {validationError && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-id="element-7466" data-mp="ddx8eiob055i">
          {validationError}
        </div>}
      <Select label="Rol" value={newUserRole} onChange={e => setNewUserRole(e.target.value as 'learner' | 'teacher' | 'parent' | 'admin')} options={[{
      value: 'learner',
      label: 'Leerling'
    }, {
      value: 'teacher',
      label: 'Docent'
    }, {
      value: 'parent',
      label: 'Ouder'
    }, {
      value: 'admin',
      label: 'Admin'
    }]} data-id="element-7467" data-mp="2cpq9aqe7puni" />
      <Input label="Naam" type="text" required value={newUserName} onChange={e => setNewUserName(e.target.value)} placeholder="Volledige naam" data-id="element-7468" data-mp="1xawa65s7sv0u" />
      <Input label="E-mailadres (optioneel)" type="email" value={newUserEmail} onChange={e => setNewUserEmail(e.target.value)} placeholder="email@voorbeeld.nl" data-id="element-7469" data-mp="269hsq0xosa1m" />
      {newUserRole === 'teacher' && <Input label="School" type="text" value={newUserSchool} onChange={e => setNewUserSchool(e.target.value)} placeholder="Naam van de school" data-id="element-7470" data-mp="3moj2xe5q4i5j" />}
      {newUserRole === 'learner' && <Select label="Groep" value={newUserGrade} onChange={e => setNewUserGrade(e.target.value)} options={[{
      value: '6',
      label: 'Groep 6'
    }, {
      value: '7',
      label: 'Groep 7'
    }, {
      value: '8',
      label: 'Groep 8'
    }]} data-id="element-7471" data-mp="2s0r5htwtaxcm" />}
      {newUserRole === 'learner' && <Input label="Wachtwoord (optioneel)" type="password" value={newUserPassword} onChange={e => setNewUserPassword(e.target.value)} placeholder="Minimaal 4 tekens" minLength={4} icon={<KeyRound className="w-4 h-4" data-id="element-7473" data-mp="1qkbajpuv52vj" />} helperText="Leerling kan ook inloggen met koppelcode (wordt automatisch gegenereerd)." data-id="element-7472" data-mp="2o0qqo85swk51" />}
      <div className="pt-4 flex justify-end gap-2" data-id="element-7474" data-mp="61hbsn76x0iz">
        <Button type="button" variant="secondary" onClick={onClose} data-id="element-7475" data-mp="aspj9doymbti">
          Annuleren
        </Button>
        <Button type="submit" data-id="element-7476" data-mp="3s9qtryery1yu">Aanmaken</Button>
      </div>
    </form>;
}